import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { platformAPI, analyticsAPI } from '../api';
import MaestraLogo from '../components/MaestraLogo';
import PlatformFooter from '../components/PlatformFooter';

const PROFESSIONS = [
  'Photographer',
  'Decorator',
  'Wedding planner',
  'Interior designer',
  'Creative studio',
  'Other',
];

const RequestAccess = () => {
  const navigate = useNavigate();
  document.title = 'Maestra - Request Access';
  const formRef = useRef(null);

  const [ptx, setPtx] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    instagram_or_website: '',
    profession: '',
    team_size: '',
    acknowledged_limited: false,
  });

  useEffect(() => {
    platformAPI.getTexts().then(res => setPtx(res.data)).catch(() => {});
    analyticsAPI.track('request_access');
  }, []);

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { data } = await platformAPI.submitAccessRequest(form);
      if (data.status === 'already_submitted') {
        setError(data.message);
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const heroTitle = ptx?.request_access_title || 'Request Access';
  const heroDesc = ptx?.request_access_description || 'Maestra is a private platform for creative professionals.\nTell us about yourself and we\'ll review your request personally.';
  const ctaLabel = ptx?.request_access_cta_label || 'Submit Request';
  const successMsg = ptx?.request_access_success_message || 'Thank you. Your request has been received.\nEach application is reviewed personally — we\'ll be in touch soon.';

  const DEFAULT_TAGS = ['Photographers', 'Decorators', 'Wedding Planners', 'Designers', 'Creative Studios'];
  const DEFAULT_BENEFITS = [
    { title: 'Organize inspiration', desc: 'Curate and structure visual references in one calm space.' },
    { title: 'Collect decisions', desc: 'Let clients react, choose, and confirm — clearly.' },
    { title: 'Reduce communication chaos', desc: 'Replace scattered messages with a single shared board.' },
    { title: 'Calm client workflow', desc: 'A refined experience your clients will appreciate.' },
  ];

  const audienceTags = (ptx?.request_access_audience_tags?.length > 0) ? ptx.request_access_audience_tags : DEFAULT_TAGS;
  const benefits = (ptx?.request_access_benefits?.length > 0) ? ptx.request_access_benefits : DEFAULT_BENEFITS;

  return (
    <div className="landing-page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>

      {/* Hero intro — logo + brand (no overlapping content) */}
      <section
        data-testid="request-access-hero-intro"
        style={{
          padding: '72px 24px 0',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <MaestraLogo size="md" />
      </section>

      {/* Hero content — separated from intro with generous vertical spacing */}
      <section
        data-testid="request-access-hero-content"
        style={{
          padding: 'clamp(80px, 14vw, 120px) 24px 64px',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto',
          width: '100%',
        }}
      >
        <h1 data-testid="request-access-title" style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 'clamp(26px, 4.5vw, 34px)',
          fontWeight: '400',
          color: 'var(--kahla-text)',
          marginTop: 0,
          marginBottom: '20px',
          lineHeight: '1.3',
        }}>{heroTitle}</h1>

        <p data-testid="request-access-description" style={{
          fontSize: '14px',
          color: 'var(--kahla-text-light)',
          lineHeight: '1.8',
          whiteSpace: 'pre-line',
          marginBottom: '32px',
        }}>{heroDesc}</p>

        <button
          className="btn-primary"
          data-testid="request-access-hero-cta"
          onClick={scrollToForm}
          style={{ padding: '12px 32px' }}
        >
          {ctaLabel}
        </button>
      </section>

      {/* Divider */}
      <div style={{ width: '40px', height: '1px', background: 'var(--kahla-border)', margin: '0 auto' }} />

      {/* Who it's for */}
      <section style={{
        padding: '64px 24px',
        maxWidth: '700px',
        margin: '0 auto',
        textAlign: 'center',
      }}>
        <h2 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '20px',
          fontWeight: '400',
          color: 'var(--kahla-text)',
          marginBottom: '32px',
        }}>Who Maestra is for</h2>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '12px',
        }}>
          {audienceTags.map(role => (
            <span key={role} style={{
              padding: '8px 20px',
              border: '1px solid var(--kahla-border)',
              fontSize: '12px',
              letterSpacing: '0.06em',
              color: 'var(--kahla-text)',
              background: 'var(--kahla-white)',
            }}>{role}</span>
          ))}
        </div>
      </section>

      {/* What Maestra helps with */}
      <section style={{
        padding: '48px 24px 64px',
        maxWidth: '600px',
        margin: '0 auto',
        textAlign: 'center',
      }}>
        <h2 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '20px',
          fontWeight: '400',
          color: 'var(--kahla-text)',
          marginBottom: '32px',
        }}>What Maestra helps with</h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px',
          textAlign: 'left',
        }}>
          {benefits.map((item, i) => (
            <div key={i} style={{
              padding: '20px',
              border: '1px solid var(--kahla-border)',
              background: 'var(--kahla-white)',
            }}>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: 'var(--kahla-text)',
                marginBottom: '6px',
              }}>{item.title}</div>
              <div style={{
                fontSize: '12px',
                color: 'var(--kahla-text-light)',
                lineHeight: '1.6',
              }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Divider */}
      <div style={{ width: '40px', height: '1px', background: 'var(--kahla-border)', margin: '0 auto' }} />

      {/* Request Form or Success */}
      <section ref={formRef} style={{
        padding: '64px 24px 80px',
        maxWidth: '480px',
        margin: '0 auto',
        width: '100%',
      }}>
        {submitted ? (
          <div data-testid="request-success" style={{ textAlign: 'center' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              border: '1px solid var(--kahla-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: '18px',
              color: 'var(--kahla-text-light)',
            }}>&#x2713;</div>
            <p data-testid="request-success-message" style={{
              fontSize: '14px',
              color: 'var(--kahla-text)',
              lineHeight: '1.8',
              whiteSpace: 'pre-line',
            }}>{successMsg}</p>
            <button
              className="btn-secondary"
              onClick={() => navigate('/')}
              style={{ marginTop: '32px', fontSize: '12px', padding: '10px 24px' }}
            >
              Back to Home
            </button>
          </div>
        ) : (
          <>
            <h2 style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: '20px',
              fontWeight: '400',
              color: 'var(--kahla-text)',
              marginBottom: '8px',
              textAlign: 'center',
            }}>{heroTitle}</h2>
            <p style={{
              fontSize: '12px',
              color: 'var(--kahla-text-light)',
              textAlign: 'center',
              marginBottom: '32px',
            }}>All fields marked with * are required</p>

            {error && (
              <div data-testid="request-error" style={{
                padding: '12px 16px',
                fontSize: '13px',
                color: '#c62828',
                background: '#ffebee',
                marginBottom: '20px',
                border: '1px solid #ffcdd2',
              }}>{error}</div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <FormField label="First name *">
                  <input
                    data-testid="request-first-name"
                    type="text"
                    value={form.first_name}
                    onChange={e => updateField('first_name', e.target.value)}
                    required
                    style={fieldStyle}
                  />
                </FormField>
                <FormField label="Last name *">
                  <input
                    data-testid="request-last-name"
                    type="text"
                    value={form.last_name}
                    onChange={e => updateField('last_name', e.target.value)}
                    required
                    style={fieldStyle}
                  />
                </FormField>
              </div>

              <FormField label="Email *">
                <input
                  data-testid="request-email"
                  type="email"
                  value={form.email}
                  onChange={e => updateField('email', e.target.value)}
                  required
                  style={fieldStyle}
                />
              </FormField>

              <FormField label="Instagram or Website">
                <input
                  data-testid="request-instagram"
                  type="text"
                  value={form.instagram_or_website}
                  onChange={e => updateField('instagram_or_website', e.target.value)}
                  placeholder="@handle or https://..."
                  style={fieldStyle}
                />
              </FormField>

              <FormField label="Profession *">
                <select
                  data-testid="request-profession"
                  value={form.profession}
                  onChange={e => updateField('profession', e.target.value)}
                  required
                  style={{ ...fieldStyle, color: form.profession ? 'var(--kahla-text)' : 'var(--kahla-text-light)' }}
                >
                  <option value="">Select your profession</option>
                  {PROFESSIONS.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </FormField>

              <FormField label="Team size (optional)">
                <input
                  data-testid="request-team-size"
                  type="text"
                  value={form.team_size}
                  onChange={e => updateField('team_size', e.target.value)}
                  placeholder="e.g. Just me, 2-5, 10+"
                  style={fieldStyle}
                />
              </FormField>

              <label data-testid="request-acknowledge" style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
                fontSize: '12px',
                color: 'var(--kahla-text-light)',
                lineHeight: '1.5',
                cursor: 'pointer',
                padding: '8px 0',
              }}>
                <input
                  type="checkbox"
                  checked={form.acknowledged_limited}
                  onChange={e => updateField('acknowledged_limited', e.target.checked)}
                  style={{ marginTop: '2px', accentColor: 'var(--kahla-text)' }}
                />
                I understand that Maestra is a curated platform with limited access.
              </label>

              <button
                type="submit"
                className="btn-primary"
                data-testid="request-submit-btn"
                disabled={submitting}
                style={{ width: '100%', marginTop: '8px', padding: '12px' }}
              >
                {submitting ? 'Submitting...' : ctaLabel}
              </button>
            </form>
          </>
        )}
      </section>

      {/* Footer */}
      <footer style={{
        padding: '24px',
        textAlign: 'center',
        borderTop: '1px solid var(--kahla-border)',
        backgroundColor: 'var(--kahla-cream)',
        marginTop: 'auto',
      }}>
        <PlatformFooter />
      </footer>
    </div>
  );
};

const fieldStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '13px',
  border: '1px solid var(--kahla-border)',
  background: 'var(--kahla-white)',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const FormField = ({ label, children }) => (
  <div>
    <label style={{
      display: 'block',
      fontSize: '11px',
      color: 'var(--kahla-text-light)',
      marginBottom: '6px',
      letterSpacing: '0.03em',
    }}>{label}</label>
    {children}
  </div>
);

export default RequestAccess;
